import { useState, useMemo } from 'react';
import { 
  Link2, 
  Copy, 
  Plus, 
  Search, 
  Share2, 
  Check, 
  SlidersHorizontal, 
  X, 
  ChevronLeft, 
  ChevronRight,
  Zap,
  Tag,
  Percent,
  TrendingUp
} from 'lucide-react';
import { 
  useAvailableProducts, 
  useAffiliateLinks, 
  useCreateLink, 
  useAvailableCategories,
  affiliateKeys 
} from '../hooks/useAffiliate';
import type { AffiliateLink, AvailableProduct } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';

type FilterTab = 'all' | 'active' | 'new';

export default function Links() {
  // --- UI State ---
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [creatingLinkForProduct, setCreatingLinkForProduct] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // --- Filter State ---
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [commissionFilter, setCommissionFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'price' | 'commissionValue'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  const queryClient = useQueryClient();

  // --- Data Fetching ---
  const { data: categoriesData } = useAvailableCategories();
  
  // Fetch links: paginated for 'active' tab, otherwise fetch first 100 to check status on 'all'/'new'
  const { data: linksResponse, isLoading: loadingLinks } = useAffiliateLinks(
    activeTab === 'active' ? { page, limit } : { limit: 100 }
  );

  const { data: productsResponse, isLoading: loadingProducts } = useAvailableProducts({
    page,
    limit,
    search: searchQuery || undefined,
    categoryId: selectedCategory || undefined,
    commissionType: commissionFilter === 'PERCENTAGE' || commissionFilter === 'FIXED' ? commissionFilter : undefined,
    isHighCommission: commissionFilter === 'HIGH' ? true : undefined,
    sortBy,
    sortOrder,
  });

  const createLinkMutation = useCreateLink();

  // --- Derived State ---
  const existingLinks: AffiliateLink[] = linksResponse?.items || [];
  const products: AvailableProduct[] = productsResponse?.items || [];
  const pagination = activeTab === 'active' ? linksResponse?.pagination : productsResponse?.pagination;

  // Manual status filtering
  const filteredProducts = useMemo(() => {
    // If Active tab, show products from our links
    if (activeTab === 'active') {
      return (linksResponse?.items || []).map(link => ({
        id: link.productId,
        title: link.productTitle,
        handle: '', // Required by AvailableProduct but not needed for display
        price: link.productPrice,
        images: link.productImage ? [{ url: link.productImage }] : [],
        commissionType: link.commissionType,
        commissionValue: link.commissionValue,
        store: { id: '', name: '', subdomain: '', logo: '' },
        categories: []
      } as unknown as AvailableProduct));
    }

    if (!products) return [];
    if (activeTab === 'all') return products;
    
    // For 'new' tab: filter out products that already have links
    return products.filter(product => {
      const hasLink = existingLinks.some(link => link.productId === product.id);
      return !hasLink;
    });
  }, [products, activeTab, linksResponse, existingLinks]);

  // Get existing link for product
  const getProductLink = (productId: string): AffiliateLink | undefined => {
    return existingLinks?.find((link) => link.productId === productId);
  };

  // --- Handlers ---
  const handleCreateLink = async (productId: string) => {
    try {
      setCreatingLinkForProduct(productId);
      const result = await createLinkMutation.mutateAsync({
        productId,
        customAlias: campaignName || undefined,
      });

      if (result?.trackingUrl) {
        const copied = await copyToClipboard(result.trackingUrl);
        showToast(copied ? 'Link copied to clipboard! ✨' : 'Link generated successfully! ✨');
      }

      await queryClient.refetchQueries({ queryKey: affiliateKeys.links() });
      setCampaignName('');
    } catch (error: any) {
      console.error('Failed to create link:', error);
      if (error?.message?.includes('already have a link')) {
        await queryClient.refetchQueries({ queryKey: affiliateKeys.links() });
        showToast('Link already exists! You can copy it now.');
      } else {
        showToast('Failed to create link. Please try again.');
      }
    } finally {
      setTimeout(() => setCreatingLinkForProduct(null), 100);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      window.focus();
      await navigator.clipboard.writeText(text);
      setCopiedLink(text);
      setTimeout(() => setCopiedLink(null), 2000);
      return true;
    } catch (error) {
      return false;
    }
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const shareLink = async (url: string, productTitle: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Check out ${productTitle}`,
          text: `I found this amazing product: ${productTitle}`,
          url: url,
        });
      } catch (error) {
        copyToClipboard(url);
        showToast('Link copied to clipboard!');
      }
    } else {
      copyToClipboard(url);
      showToast('Link copied to clipboard!');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setCommissionFilter('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
    setShowMobileFilters(false);
  };

  const activeFilterCount = [
    searchQuery,
    selectedCategory,
    commissionFilter,
    sortBy !== 'createdAt' || sortOrder !== 'desc' ? 'sort' : ''
  ].filter(Boolean).length;

  return (
    <div className="space-y-8 p-6 pb-20 relative min-h-screen">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-[100] animate-in slide-in-from-top-2">
          <div className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 border border-zinc-800 dark:border-zinc-200">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="font-semibold text-sm">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-zinc-200 dark:border-zinc-800 pb-8">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-3 bg-gradient-to-r from-zinc-900 to-zinc-500 dark:from-white dark:to-zinc-500 bg-clip-text text-transparent">
            Product Links
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 text-lg max-w-lg leading-relaxed">
            Find products and get your personal links to start earning.
          </p>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-zinc-100 dark:bg-zinc-900/50 p-1 rounded-2xl border border-zinc-200/50 dark:border-zinc-800/50 self-start md:self-auto shadow-sm">
          {(['all', 'active', 'new'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setPage(1);
              }}
              className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all duration-200 ${
                activeTab === tab
                  ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md'
                  : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
              }`}
            >
              {tab === 'all' ? 'All' : tab === 'active' ? 'Active' : 'New'}
            </button>
          ))}
        </div>
      </div>

      {/* Main Filter Bar */}
      <div className="flex flex-col lg:flex-row items-center gap-4">
        {/* Search Input Expanded */}
        <div className="relative flex-1 group w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" />
          <input
            type="text"
            placeholder="Search products by title, store or handle..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full pl-12 pr-4 py-4 md:py-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-base focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none shadow-sm"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full text-zinc-400 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Desktop Filter Tools */}
        <div className="hidden lg:flex items-center gap-3">
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
              className="appearance-none pl-10 pr-10 py-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-emerald-500/20 outline-none transition shadow-sm"
            >
              <option value="">All Categories</option>
              {categoriesData?.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} {cat.store?.name ? `(${cat.store.name})` : ''}
                </option>
              ))}
            </select>
            <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={commissionFilter}
              onChange={(e) => {
                setCommissionFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none pl-10 pr-10 py-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-emerald-500/20 outline-none transition shadow-sm"
            >
              <option value="">Commission: Any</option>
              <option value="HIGH">High Earnings (10%+)</option>
              <option value="PERCENTAGE">Percentage Only</option>
              <option value="FIXED">Fixed Only</option>
            </select>
            <Percent className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSort, newOrder] = e.target.value.split('-') as [any, any];
                setSortBy(newSort);
                setSortOrder(newOrder);
                setPage(1);
              }}
              className="appearance-none pl-10 pr-10 py-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-emerald-500/20 outline-none transition shadow-sm"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="commissionValue-desc">Earn: High to Low</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="price-asc">Price: Low to High</option>
            </select>
            <TrendingUp className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="h-full px-6 py-4 text-emerald-600 dark:text-emerald-400 text-sm font-bold hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-2xl transition"
            >
              Reset
            </button>
          )}
        </div>

        {/* Mobile Filter Toggle */}
        <button
          onClick={() => setShowMobileFilters(true)}
          className="lg:hidden flex items-center justify-center gap-2 w-full py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-bold transition-transform active:scale-95"
        >
          <SlidersHorizontal className="w-5 h-5" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="ml-2 w-6 h-6 bg-emerald-500 text-white rounded-full text-[10px] flex items-center justify-center border-2 border-zinc-900 dark:border-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile Filters Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[110] lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white dark:bg-zinc-950 rounded-t-[32px] p-8 space-y-8 animate-in slide-in-from-bottom-full duration-300">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-black">Filters</h2>
              <button 
                onClick={() => setShowMobileFilters(false)}
                className="p-3 bg-zinc-100 dark:bg-zinc-900 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <label className="text-xs font-black uppercase tracking-widest text-zinc-400">Category</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setSelectedCategory(''); setPage(1); }}
                    className={`p-4 rounded-2xl text-sm font-bold border-2 transition ${selectedCategory === '' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600' : 'border-zinc-100 dark:border-zinc-800'}`}
                  >
                    All
                  </button>
                  {categoriesData?.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
                      className={`p-4 rounded-2xl text-sm font-bold border-2 transition line-clamp-1 ${selectedCategory === cat.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600' : 'border-zinc-100 dark:border-zinc-800'}`}
                    >
                      {cat.name} {cat.store?.name ? `(${cat.store.name})` : ''}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-black uppercase tracking-widest text-zinc-400">Commission</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { label: 'Any', value: '' },
                    { label: 'High', value: 'HIGH' },
                    { label: 'Percentage', value: 'PERCENTAGE' },
                    { label: 'Fixed', value: 'FIXED' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setCommissionFilter(opt.value); setPage(1); }}
                      className={`px-4 py-2.5 rounded-full text-xs font-bold border-2 transition ${commissionFilter === opt.value ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-zinc-100 dark:border-zinc-800 text-zinc-500'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-black uppercase tracking-widest text-zinc-400">Sort by</label>
                <select 
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSort, newOrder] = e.target.value.split('-') as [any, any];
                    setSortBy(newSort);
                    setSortOrder(newOrder);
                    setPage(1);
                  }}
                  className="w-full p-4 bg-zinc-100 dark:bg-zinc-900 border-0 rounded-2xl font-bold outline-none"
                >
                  <option value="createdAt-desc">Newest</option>
                  <option value="commissionValue-desc">High commission</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="price-asc">Price: Low to High</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4 pb-2">
              <button
                onClick={clearFilters}
                className="flex-1 py-4 bg-zinc-100 dark:bg-zinc-900 rounded-2xl font-bold uppercase tracking-widest text-sm"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="flex-[2] py-4 bg-emerald-600 text-white rounded-2xl font-bold uppercase tracking-widest text-sm shadow-lg shadow-emerald-500/20"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {loadingProducts || (activeTab === 'active' && loadingLinks) ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden animate-pulse h-[400px]">
              <div className="aspect-square bg-zinc-100 dark:bg-zinc-800" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-zinc-100 dark:bg-zinc-800 rounded-full w-3/4" />
                <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded-full w-1/2" />
                <div className="pt-4 h-10 bg-zinc-100 dark:bg-zinc-800 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredProducts && filteredProducts.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4 lg:gap-6">
            {filteredProducts.map((product) => {
              const productLink = getProductLink(product.id);
              const hasExistingLink = !!productLink;

              return (
                <div
                  key={product.id}
                  className="group bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-3xl overflow-hidden hover:shadow-2xl hover:shadow-zinc-500/10 transition-all duration-500 flex flex-col h-full border-t-0 hover:-translate-y-1"
                >
                  {/* Product Image */}
                  <div className="aspect-square bg-zinc-50 dark:bg-zinc-800 relative overflow-hidden">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0].url}
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Link2 className="w-10 h-10 text-zinc-200 dark:text-zinc-700" />
                      </div>
                    )}
                    
                    {/* Status Floating Badge */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                      {hasExistingLink && (
                        <div className="bg-emerald-600 text-[9px] text-white px-3 py-1.5 rounded-full font-black flex items-center gap-1.5 shadow-xl backdrop-blur-md border border-emerald-500/50">
                          <Zap className="w-2.5 h-2.5 fill-current" />
                          ACTIVE LINK
                        </div>
                      )}
                      {!hasExistingLink && (
                        <div className="bg-zinc-900/80 text-[9px] text-white px-3 py-1.5 rounded-full font-black flex items-center gap-1.5 shadow-xl backdrop-blur-md border border-zinc-700/50 uppercase tracking-tight">
                          Not linked
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-3 flex flex-col flex-1 gap-2">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-sm line-clamp-2 text-zinc-900 dark:text-zinc-100 leading-snug group-hover:text-emerald-600 transition-colors">
                        {product.title}
                      </h3>
                    </div>

                    <div className="space-y-2.5">
                      {/* Price & Upside Section */}
                      <div className="p-2.5 bg-zinc-50/80 dark:bg-zinc-800/30 rounded-xl flex items-center justify-between gap-1.5 border border-zinc-100/50 dark:border-zinc-800/50">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-medium text-zinc-400 uppercase">Price</span>
                          <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
                            KES {product.price.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[8px] font-medium text-emerald-500 uppercase tracking-tight">Earn</span>
                          <span className="text-xs font-semibold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded-md">
                            {product.commissionType === 'PERCENTAGE'
                              ? `${product.commissionValue}%`
                              : `KES ${product.commissionValue}`}
                          </span>
                        </div>
                      </div>

                      {/* Action Area */}
                      <div className="pt-2">
                        {hasExistingLink ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-1.5">
                              <button
                                onClick={async () => {
                                  await copyToClipboard(productLink.trackingUrl);
                                  showToast('Link copied! ✨');
                                }}
                                className="flex items-center justify-center gap-1.5 h-9 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl text-[9px] font-semibold uppercase tracking-tight hover:scale-[1.02] active:scale-[0.98] transition-all shadow-md group/btn"
                              >
                                {copiedLink === productLink.trackingUrl ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3 group-hover/btn:scale-110 transition-transform" />}
                                {copiedLink === productLink.trackingUrl ? 'OK' : 'Copy'}
                              </button>
                              <button
                                onClick={() => shareLink(productLink.trackingUrl, product.title)}
                                className="flex items-center justify-center gap-1.5 h-9 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-xl text-[9px] font-semibold uppercase tracking-tight hover:scale-[1.02] hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all border border-zinc-200/50 dark:border-zinc-700/50"
                              >
                                <Share2 className="w-3 h-3" />
                                Share
                              </button>
                            </div>

                            {/* Minimal Stats Card */}
                            <div className="flex items-center justify-between px-2 py-2 bg-zinc-900/5 dark:bg-zinc-100/5 rounded-xl">
                              <div className="text-center group/stat">
                                <p className="text-[10px] font-semibold text-zinc-900 dark:text-zinc-100 leading-none mb-0.5">{productLink.totalClicks}</p>
                                <p className="text-[7px] text-zinc-500 font-medium uppercase tracking-tighter opacity-60">Clicks</p>
                              </div>
                              <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-800" />
                              <div className="text-center group/stat">
                                <p className="text-[10px] font-semibold text-zinc-900 dark:text-zinc-100 leading-none mb-0.5">{productLink.totalSales}</p>
                                <p className="text-[7px] text-zinc-500 font-medium uppercase tracking-tighter opacity-60">Sales</p>
                              </div>
                              <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-800" />
                              <div className="text-center group/stat">
                                <p className="text-[10px] font-semibold text-emerald-600 leading-none mb-0.5">{Math.floor(Number(productLink.totalCommissions || 0))}</p>
                                <p className="text-[7px] text-emerald-500 font-medium uppercase tracking-tighter opacity-60">Earned</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleCreateLink(product.id)}
                            disabled={creatingLinkForProduct === product.id}
                            className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-100 dark:disabled:bg-zinc-800 text-white font-semibold rounded-xl text-[9px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-[0.98]"
                          >
                            {creatingLinkForProduct === product.id ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-3 h-3 stroke-[3]" />
                                Generate link
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 py-10 border-t border-zinc-100 dark:border-zinc-800">
              <p className="text-sm font-bold text-zinc-500">
                Showing <span className="text-zinc-900 dark:text-zinc-100">{(page - 1) * limit + 1}</span> to <span className="text-zinc-900 dark:text-zinc-100">{Math.min(page * limit, pagination.total)}</span> of <span className="text-zinc-900 dark:text-zinc-100">{pagination.total}</span> products
              </p>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-12 h-12 flex items-center justify-center rounded-2xl border-2 border-zinc-100 dark:border-zinc-800 text-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:border-zinc-300 transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(num => num === 1 || num === pagination.totalPages || (num >= page - 1 && num <= page + 1))
                    .map((num, i, arr) => (
                      <div key={num} className="flex items-center gap-2">
                        {i > 0 && arr[i-1] !== num - 1 && <span className="text-zinc-300">...</span>}
                        <button
                          onClick={() => setPage(num)}
                          className={`w-12 h-12 rounded-2xl font-black text-sm transition-all ${
                            page === num
                              ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-xl scale-110'
                              : 'text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100'
                          }`}
                        >
                          {num}
                        </button>
                      </div>
                    ))}
                </div>

                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="w-12 h-12 flex items-center justify-center rounded-2xl border-2 border-zinc-100 dark:border-zinc-800 text-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:border-zinc-300 transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 border-dashed rounded-[40px] p-20 mt-12">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-32 h-32 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner animate-bounce duration-[3000ms]">
              <Search className="w-16 h-16 text-zinc-300 dark:text-zinc-600" />
            </div>
            <h3 className="text-2xl font-black text-zinc-900 dark:text-zinc-100 mb-3">No products found</h3>
            <p className="text-zinc-500 dark:text-zinc-400 font-medium leading-relaxed mb-8">
              {activeTab === 'active' 
                ? "You haven't generated any links for these filters yet."
                : "We couldn't find any products matching your filters. Try search for something else or clearing filters."}
            </p>
            <button
              onClick={clearFilters}
              className="px-10 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-xs hover:scale-105 transition-transform"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
